<script setup lang="ts">
import { ref, computed } from "vue";

export interface RadioGroupOption {
  value: string | number;
  title: string;
  subtitle?: string;
  disabled?: boolean;
}

interface Props {
  modelValue?: string | number | null;
  options: RadioGroupOption[];
  disabled?: boolean;
  /** Accessible name for the group (maps to the radiogroup's aria-label). */
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  disabled: false,
  ariaLabel: undefined,
});

const emit = defineEmits<{
  (event: "update:modelValue", value: string | number): void;
  (event: "change", value: string | number): void;
}>();

const rootRef = ref<HTMLElement | null>(null);

function isDisabled(option: RadioGroupOption): boolean {
  return props.disabled || Boolean(option.disabled);
}

function isSelected(option: RadioGroupOption): boolean {
  return props.modelValue === option.value;
}

// Roving tabindex: exactly one row is tab-focusable — the selected one, or the
// first enabled row when nothing is selected yet.
const tabbableValue = computed<string | number | null>(() => {
  const selected = props.options.find((o) => isSelected(o) && !isDisabled(o));
  if (selected) return selected.value;
  const firstEnabled = props.options.find((o) => !isDisabled(o));
  return firstEnabled ? firstEnabled.value : null;
});

function select(option: RadioGroupOption): void {
  if (isDisabled(option) || isSelected(option)) return;
  emit("update:modelValue", option.value);
  emit("change", option.value);
}

function focusRow(index: number): void {
  const rows = rootRef.value?.querySelectorAll<HTMLElement>('[role="radio"]');
  rows?.[index]?.focus();
}

function onKeydown(event: KeyboardEvent, index: number): void {
  const enabled = props.options
    .map((option, i) => ({ option, i }))
    .filter(({ option }) => !isDisabled(option))
    .map(({ i }) => i);
  if (enabled.length === 0) return;

  if (event.key === " " || event.key === "Enter") {
    event.preventDefault();
    select(props.options[index]);
    return;
  }

  const pos = enabled.indexOf(index);
  let target: number | null = null;
  if (event.key === "ArrowDown" || event.key === "ArrowRight") {
    target = enabled[(pos + 1) % enabled.length];
  } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
    target = enabled[(pos - 1 + enabled.length) % enabled.length];
  } else {
    return;
  }

  event.preventDefault();
  select(props.options[target]);
  focusRow(target);
}
</script>

<template>
  <div
    ref="rootRef"
    class="pj-radio-group"
    role="radiogroup"
    :aria-label="ariaLabel"
  >
    <div
      v-for="(option, index) in options"
      :key="option.value"
      class="pj-radio-group__option"
      :class="{
        'pj-radio-group__option--selected': isSelected(option),
        'pj-radio-group__option--disabled': isDisabled(option),
      }"
      role="radio"
      :aria-checked="isSelected(option)"
      :aria-disabled="isDisabled(option) || undefined"
      :tabindex="!isDisabled(option) && option.value === tabbableValue ? 0 : -1"
      @click="select(option)"
      @keydown="onKeydown($event, index)"
    >
      <span class="pj-radio-group__content">
        <span class="pj-radio-group__title">{{ option.title }}</span>
        <span v-if="option.subtitle" class="pj-radio-group__subtitle">
          {{ option.subtitle }}
        </span>
      </span>
      <span class="pj-radio-group__indicator" aria-hidden="true" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pj-radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--pj-space-2);
  width: 100%;
}

.pj-radio-group__option {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--pj-space-3);
  width: 100%;
  padding: var(--pj-space-4);
  border-radius: var(--pj-radius-xl);
  border: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
  background: var(--pj-color-brand-white, #ffffff);
  cursor: pointer;
  user-select: none;
  transition:
    border-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    background-color var(--pj-motion-duration-fast)
      var(--pj-motion-easing-standard);

  &--selected {
    border-color: var(--pj-color-brand-gray-850, #222926);
    box-shadow: inset 0 0 0 1px var(--pj-color-brand-gray-850, #222926);
  }

  &--disabled {
    cursor: not-allowed;
    background: var(--pj-color-brand-gray-150, #f1f0ee);
    border-color: var(--pj-color-brand-gray-350, #c6c7bf);
  }

  &--disabled .pj-radio-group__title,
  &--disabled .pj-radio-group__subtitle {
    color: var(--pj-color-brand-gray-550, #74756f);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }

  &--selected:focus-visible {
    box-shadow:
      inset 0 0 0 1px var(--pj-color-brand-gray-850, #222926),
      0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }
}

.pj-radio-group__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.pj-radio-group__title {
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-medium);
  line-height: 20px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.pj-radio-group__subtitle {
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: var(--pj-font-weight-regular);
  line-height: 22px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-550, #74756f);
}

/* Radio indicator — mirrors the standalone radio dot: a grey ring that becomes
   a thick green ring when selected. */
.pj-radio-group__indicator {
  box-sizing: border-box;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--pj-color-brand-gray-350, #c6c7bf);
  background: var(--pj-color-brand-white, #ffffff);
  transition:
    border-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    border-width var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);

  .pj-radio-group__option--selected & {
    border-color: var(--pj-color-brand-green-450, #2fad4c);
    border-width: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pj-radio-group__option,
  .pj-radio-group__indicator {
    transition: none;
  }
}
</style>
